'use client';

import { useEffect, useState } from 'react';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Input from '@/components/molecules/input/input';
import GenericAccordion from '@/components/organisms/generic-accordion/generic-accordion';
import Link from '@/components/utilities/link/link';
import useMutationAddPreBookingGiftCard from '@/integrations/booking/pre-booking/gift-cards/add-gift-card/add-gift-card.hooks';
import useMutationDeletePreBookingGiftCard from '@/integrations/booking/pre-booking/gift-cards/delete-gift-card/delete-gift-card.hooks';
import useQueryGiftCard from '@/integrations/content/content-modules/gift-card/gift-card.hooks';
import { QueryId } from '@/integrations/content/utils';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import styles from './gift-card.module.scss';

export interface GiftCardProps {
  /** The UUID of the module */
  id: Content.Components['GiftCardHeadlessModuleDto']['id'];
  /** The UUID of the current page */
  pageId: Content.Components['GiftCardHeadlessModuleDto']['pageId'];
  /** The parameters for the booking */
  parameters: Content.Components['GiftCardHeadlessModuleDto']['parameters'];
}

const GiftCard = ({ id, pageId, parameters }: GiftCardProps) => {
  const { t } = useI18n();
  const { brand } = usePageDetailsContext();
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardError, setGiftCardError] = useState('');

  const identifiers = { ...parameters, id, pageId };
  const invalidationKeys = [QueryId.GIFT_CARD, QueryId.PASSENGER_SUMMARY];

  const {
    data: dataGiftCard,
    isFetching: isFetchingGiftCard,
    isLoading: isLoadingGiftCard,
  } = useQueryGiftCard({
    parameters: identifiers,
  });

  const hasNoData = !isFetchingGiftCard && !dataGiftCard;

  const {
    data: dataAddGiftCard,
    isLoading: isMutatingAddPreBookingGiftCard,
    mutate: mutateAddPreBookingGiftCard,
  } = useMutationAddPreBookingGiftCard();

  const {
    data: dataDeleteGiftCard,
    isLoading: isMutatingDeletePreBookingGiftCard,
    mutate: mutateDeletePreBookingGiftCard,
  } = useMutationDeletePreBookingGiftCard();

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGiftCardCode(event.target.value);
  };

  const handleSubmitGiftCardCode = () => {
    if (!giftCardCode) return;

    mutateAddPreBookingGiftCard({
      brand,
      giftCard: giftCardCode,
      invalidationKeys: invalidationKeys,
      parameters: identifiers,
    });
  };

  const removeGiftCardCode = (giftCard: string): void => {
    if (!giftCard) return;

    mutateDeletePreBookingGiftCard({
      brand,
      giftCard: giftCard,
      invalidationKeys: invalidationKeys,
      parameters: identifiers,
    });
  };

  useEffect(() => {
    if (!dataAddGiftCard) return;

    if (!dataAddGiftCard?.result) {
      setGiftCardError(t('items.modules.bookingFlow.giftCardCodeError'));
    } else {
      setGiftCardCode('');
      setGiftCardError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAddGiftCard]);

  useEffect(() => {
    if (!dataDeleteGiftCard) return;

    if (!dataDeleteGiftCard?.result) {
      setGiftCardError(t('items.modules.bookingFlow.giftCardCodeError'));
    } else {
      setGiftCardError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataDeleteGiftCard]);

  if (hasNoData) return null;

  return (
    <div className={styles.component}>
      {isLoadingGiftCard ? (
        <SkeletonLoader borderRadius="8px" height="28px" width="100%" />
      ) : (
        <GenericAccordion
          headline={dataGiftCard?.headline ?? ''}
          headlineTagStyle="headingSmall"
        >
          <div className={styles.contentWrapper}>
            <Input
              error={giftCardError}
              hasClear
              label={t('items.modules.bookingFlow.giftCardCode')}
              onChange={handleInputChange}
              onClear={() => setGiftCardCode('')}
              onEnter={handleSubmitGiftCardCode}
              value={giftCardCode}
            />

            {dataGiftCard && dataGiftCard.addedGiftCardCodes.length > 0 && (
              <div className={styles.addedGiftCardCodesWrapper}>
                {dataGiftCard.addedGiftCardCodes.map(addedGiftCardCode => (
                  <div
                    key={addedGiftCardCode}
                    className={styles.addedGiftCardCode}
                  >
                    <Typography
                      className={styles.addedGiftCardCodeText}
                      tagStyle="bodySmall"
                    >
                      {`${addedGiftCardCode} ${t('items.common.words.wasAdded')}`}
                    </Typography>

                    <Link
                      className={styles.removeGiftCardCodeLink}
                      onClick={() => removeGiftCardCode(addedGiftCardCode)}
                    >
                      <Typography tagStyle="bodySmall">
                        {t('items.common.words.remove')}
                      </Typography>
                    </Link>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.buttonWrapper}>
              <Button
                className={styles.button}
                isLoading={
                  isFetchingGiftCard ||
                  isMutatingAddPreBookingGiftCard ||
                  isMutatingDeletePreBookingGiftCard
                }
                onClick={handleSubmitGiftCardCode}
                text={t('items.common.words.add')}
                variant="primary"
              />
            </div>
          </div>
        </GenericAccordion>
      )}
    </div>
  );
};

GiftCard.displayName = 'GiftCard';

export default GiftCard;
